<template>
  <USelectMenu
    v-model="currentValue"
    :items="items"
    label-key="displayName"
    :by="compareItems"
    :filter-fields="['displayName']"
    :ui="{ content: 'min-w-72 max-w-[calc(100vw-1rem)]' }"
  >
    <template #default="{ modelValue: selected }">
      <div class="flex items-center pr-4 text-sm">
        <template v-if="selected && !isAllItem(selected)">
          <o-avatar :items="[selected]" class="pr-2" />
          <p>
            {{ selected.displayName }}
          </p>
        </template>

        <template v-else>
          <p class="hidden 2xl:block">
            {{ $t('employee.all') }}
          </p>
          <p class="block 2xl:hidden">
            {{ $t('general.all') }}
          </p>
        </template>
      </div>
    </template>

    <template #item="{ item }">
      <div class="flex items-center">
        <o-avatar v-if="!isAllItem(item)" :items="[item]" class="pr-2" />
        <p class="text-sm">
          {{ item.displayName }}
        </p>
      </div>
    </template>
  </USelectMenu>
</template>

<script setup lang="ts">
import {
  OAvatar,
} from '@/components/elements'

import { EmployeeType } from '@/_api/models/employee'

import { useEmployeeStore } from '~/stores/useEmployeeStore'

const { t: $t } = useI18n()
const employeeStore = useEmployeeStore()

interface Props {
  modelValue?: any[] | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any[]): void
}>()

const allItem = computed(() => ({
  _sentinel: true,
  displayName: $t('employee.all'),
}))

function isAllItem(item: any): boolean {
  return item?._sentinel === true
}

function compareItems(a: any, b: any): boolean {
  if (a === b) return true
  if (!a || !b) return false
  if (a._sentinel && b._sentinel) return true
  if (a.idEmployee && b.idEmployee) return a.idEmployee === b.idEmployee
  return false
}

const currentValue = computed({
  get() {
    const emp = props.modelValue?.[0] || null
    return emp || allItem.value
  },
  set(value: any) {
    if (isAllItem(value)) {
      emit('update:modelValue', [])
    } else {
      emit('update:modelValue', value ? [value] : [])
    }
  },
})

const employees = computed(() => employeeStore.all)

const items = computed(() => {
  const emps = employees.value.filter((employee: any) => {
    return employee.type === EmployeeType.Employee
  })

  return [allItem.value, { type: 'separator' as const }, ...emps]
})

onMounted(() => {
  employeeStore.sync()
})
</script>
